/**
 * Subscription Service for handling payment transactions and account tier updates
 * Integrates with Supabase payment_transactions and profiles tables
 */

import { supabase } from "@/integrations/supabase/client";

export interface PaymentTransaction {
  id?: string;
  user_id: string;
  subscription_id?: string;
  provider: "stripe" | "paystack" | "payfast";
  provider_transaction_id: string;
  amount: number;
  currency: string;
  status: "pending" | "completed" | "failed" | "refunded";
  metadata?: Record<string, any>;
  created_at?: string;
}

export interface UserSubscription {
  id?: string;
  user_id: string;
  plan_id: string;
  status: "active" | "cancelled" | "pending" | "expired";
  payment_provider: "stripe" | "paystack" | "payfast" | "none";
  transaction_id?: string;
  amount?: number;
  currency?: string;
  started_at?: string;
  next_billing_date?: string;
  cancelled_at?: string;
  created_at?: string;
  updated_at?: string;
}

export type AccountTier = "PAYG" | "paid";

class SubscriptionService {
  /**
   * Create a new payment transaction record
   */
  async createPaymentTransaction(
    transaction: Omit<PaymentTransaction, "id" | "created_at">,
  ): Promise<PaymentTransaction | null> {
    try {
      const { data, error } = await supabase
        .from("payment_transactions")
        .insert({
          user_id: transaction.user_id,
          subscription_id: transaction.subscription_id || null,
          provider: transaction.provider,
          provider_transaction_id: transaction.provider_transaction_id,
          amount: transaction.amount,
          currency: transaction.currency,
          status: transaction.status,
          metadata: transaction.metadata || null,
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating payment transaction:", error);
        return null;
      }

      return data as PaymentTransaction;
    } catch (error) {
      console.error("Error in createPaymentTransaction:", error);
      return null;
    }
  }

  /**
   * Update an existing payment transaction
   */
  async updatePaymentTransaction(
    transactionId: string,
    updates: Partial<PaymentTransaction>,
  ): Promise<PaymentTransaction | null> {
    try {
      const { data, error } = await supabase
        .from("payment_transactions")
        .update(updates)
        .eq("id", transactionId)
        .select()
        .single();

      if (error) {
        console.error("Error updating payment transaction:", error);
        return null;
      }

      return data as PaymentTransaction;
    } catch (error) {
      console.error("Error in updatePaymentTransaction:", error);
      return null;
    }
  }

  /**
   * Create default free subscription for new users
   */
  async createDefaultSubscription(
    userId: string,
  ): Promise<UserSubscription | null> {
    try {
      console.log("Creating default PAYG subscription for user:", userId);

      // Check if we have a valid session
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        console.error(
          "No active session - cannot create subscription due to RLS",
        );
        return null;
      }

      const defaultSubscription = {
        user_id: userId,
        plan_id: "PAYG",
        status: "active" as const,
        payment_provider: "none" as const, // Use 'none' for PAYG default
        transaction_id: null,
        amount: 0.0,
        currency: "ZAR",
        started_at: new Date().toISOString(),
        next_billing_date: null, // Free plan doesn't have billing
        cancelled_at: null,
      };

      console.log(
        "Attempting to insert subscription with data:",
        defaultSubscription,
      );

      const { data, error } = await supabase
        .from("user_subscriptions")
        .insert(defaultSubscription)
        .select()
        .single();

      if (error) {
        console.error("Error creating default subscription:", error);
        console.error("Full error details:", JSON.stringify(error, null, 2));
        return null;
      }

      console.log("Default subscription created successfully:", data);
      return data as UserSubscription;
    } catch (error) {
      console.error("Error in createDefaultSubscription:", error);
      return null;
    }
  }

  /**
   * Initialize user subscription when they first sign up
   */
  async initializeUserSubscription(
    userId: string,
  ): Promise<UserSubscription | null> {
    try {
      // Check if user already has any subscription
      const { data: existingSubscription } = await supabase
        .from("user_subscriptions")
        .select("id")
        .eq("user_id", userId)
        .limit(1)
        .maybeSingle();

      if (existingSubscription) {
        console.log("User already has subscription, skipping initialization");
        return await this.getUserActiveSubscription(userId);
      }

      // Create default free subscription
      return await this.createDefaultSubscription(userId);
    } catch (error) {
      console.error("Error in initializeUserSubscription:", error);
      return null;
    }
  }

  /**
   * Get payment transaction by provider transaction ID
   */
  async getPaymentTransactionByProviderId(
    providerId: string,
  ): Promise<PaymentTransaction | null> {
    try {
      const { data, error } = await supabase
        .from("payment_transactions")
        .select("*")
        .eq("provider_transaction_id", providerId)
        .single();

      if (error) {
        console.error("Error fetching payment transaction:", error);
        return null;
      }

      return data as PaymentTransaction;
    } catch (error) {
      console.error("Error in getPaymentTransactionByProviderId:", error);
      return null;
    }
  }

  /**
   * Create or update user subscription
   */
  async createUserSubscription(
    subscription: Omit<UserSubscription, "id" | "created_at" | "updated_at">,
  ): Promise<UserSubscription | null> {
    try {
      // Check if user already has an active subscription
      const { data: existingSubscription } = await supabase
        .from("user_subscriptions")
        .select("*")
        .eq("user_id", subscription.user_id)
        .eq("status", "active")
        .single();

      // If user has an active subscription, update it instead
      if (existingSubscription) {
        const { data, error } = await supabase
          .from("user_subscriptions")
          .update({
            plan_id: subscription.plan_id,
            payment_provider: subscription.payment_provider,
            transaction_id: subscription.transaction_id,
            external_subscription_id: subscription.external_subscription_id,
            amount_paid: subscription.amount_paid,
            currency: subscription.currency,
            billing_cycle: subscription.billing_cycle,
            next_billing_date: subscription.next_billing_date,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingSubscription.id)
          .select()
          .single();

        if (error) {
          console.error("Error updating user subscription:", error);
          return null;
        }

        return data as UserSubscription;
      } else {
        // Create new subscription
        const { data, error } = await supabase
          .from("user_subscriptions")
          .insert([
            {
              ...subscription,
              started_at: new Date().toISOString(),
            },
          ])
          .select()
          .single();

        if (error) {
          console.error("Error creating user subscription:", error);
          return null;
        }

        return data as UserSubscription;
      }
    } catch (error) {
      console.error("Error in createUserSubscription:", error);
      return null;
    }
  }

  /**
   * Update user account tier in profiles table
   */
  async updateUserAccountTier(
    userId: string,
    accountTier: AccountTier,
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          account_tier: accountTier,
          updated_at: new Date().toISOString(),
        })
        .eq("id", userId);

      if (error) {
        console.error("Error updating user account tier:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error in updateUserAccountTier:", error);
      return false;
    }
  }

  /**
   * Get user's current account tier
   */
  async getUserAccountTier(userId: string): Promise<AccountTier | null> {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("account_tier")
        .eq("id", userId)
        .single();

      if (error) {
        console.error("Error fetching user account tier:", error);
        return null;
      }

      return (data?.account_tier as AccountTier) || "PAYG";
    } catch (error) {
      console.error("Error in getUserAccountTier:", error);
      return null;
    }
  }

  /**
   * Get user's current active subscription
   */
  async getUserActiveSubscription(
    userId: string,
  ): Promise<UserSubscription | null> {
    try {
      // Check if we have a valid session first
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        console.warn(
          "No active session - cannot fetch subscription due to RLS",
        );
        return null;
      }

      const { data, error } = await supabase
        .from("user_subscriptions")
        .select("*")
        .eq("user_id", userId)
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(); // Use maybeSingle() instead of single() to handle no results

      if (error) {
        console.error("Error fetching user subscription:", error);
        console.error("Full error details:", JSON.stringify(error, null, 2));
        return null;
      }

      // If no active subscription found, create default PAYG subscription
      if (!data) {
        console.log(
          "No active subscription found, creating default PAYG subscription...",
        );
        return await this.createDefaultSubscription(userId);
      }

      return data as UserSubscription;
    } catch (error) {
      console.error("Error in getUserActiveSubscription:", error);
      return null;
    }
  }

  /**
   * Process successful payment and update user subscription and account tier
   */
  async processSuccessfulPayment(
    userId: string,
    planId: string,
    transactionDetails: {
      provider: "stripe" | "paystack" | "payfast";
      provider_transaction_id: string;
      amount: number;
      currency: string;
      payment_details?: Record<string, any>;
    },
  ): Promise<{
    success: boolean;
    subscription?: UserSubscription;
    transaction?: PaymentTransaction;
    error?: string;
  }> {
    try {
      // Idempotency guard: if a completed transaction with this provider_transaction_id
      // already exists, skip processing to avoid duplicate allocations.
      const existingTx = await this.getPaymentTransactionByProviderId(
        transactionDetails.provider_transaction_id,
      );
      if (existingTx && existingTx.status === "completed") {
        console.log(
          "Idempotency: existing completed transaction found, skipping processing:",
          existingTx,
        );
        return {
          success: true,
          subscription: null,
          transaction: existingTx,
        };
      }
      // 1. Create payment transaction record
      const transaction = await this.createPaymentTransaction({
        user_id: userId,
        provider: transactionDetails.provider,
        provider_transaction_id: transactionDetails.provider_transaction_id,
        amount: transactionDetails.amount,
        currency: transactionDetails.currency,
        status: "completed",
        metadata: {
          plan_id: planId,
          payment_details: transactionDetails.payment_details || {},
          processed_at: new Date().toISOString(),
        },
      });

      if (!transaction) {
        return {
          success: false,
          error: "Failed to create payment transaction",
        };
      }

      // Check if this is a credit purchase (should not create subscription)
      const isCreditsPurchase = planId.startsWith("credits-");

      let subscription = null;

      if (isCreditsPurchase) {
        // For credit purchases: ONLY add credits to user profile, do not create subscription
        console.log(`Processing credit purchase: ${planId}`);

        // Skip subscription creation and tier updates for credit purchases
        // Credits will be allocated in step 4 below
      } else {
        // For subscription plans: create or update user subscription
        subscription = await this.createUserSubscription({
          user_id: userId,
          plan_id: planId,
          status: "active",
          payment_provider: transactionDetails.provider,
          transaction_id: transaction.id?.toString(),
          amount: transactionDetails.amount,
          currency: transactionDetails.currency,
        });

        if (!subscription) {
          return {
            success: false,
            error: "Failed to create user subscription",
          };
        }

        // Update payment transaction with subscription ID (only for subscriptions)
        await this.updatePaymentTransaction(transaction.id!, {
          subscription_id: subscription.id,
        });
      }

      // 3. Update user account tier based on plan
      const normalizedPlan = (planId || "").toLowerCase();
      const newAccountTier: AccountTier =
        normalizedPlan === "payg" ? "PAYG" : "paid";
      const tierUpdateSuccess = await this.updateUserAccountTier(
        userId,
        newAccountTier,
      );

      if (!tierUpdateSuccess) {
        console.warn(
          "Failed to update user account tier, but payment was processed successfully",
        );
      }

      // 4. Allocate credits based on plan type
      try {
        // Call Supabase Edge Function to allocate credits
        const { data: result, error: edgeError } =
          await supabase.functions.invoke("allocate-credits", {
            body: {
              user_id: userId,
              plan_id: planId,
              regular_credits:
                normalizedPlan === "professional"
                  ? 80
                  : planId.startsWith("credits-")
                    ? parseInt(planId.split("-")[1])
                    : 0,
              bonus_credits: normalizedPlan === "professional" ? 20 : 0,
            },
          });
        if (edgeError || !result?.success) {
          console.error(
            "Edge function failed to allocate credits:",
            edgeError || result?.error,
          );
        } else {
          console.log("✅ Edge function allocated credits:", result);
        }
      } catch (err) {
        console.error(
          "Error during credits allocation via edge function:",
          err,
        );
      }

      return {
        success: true,
        subscription,
        transaction: { ...transaction, subscription_id: subscription?.id },
      };
    } catch (error) {
      console.error("Error processing successful payment:", error);
      return { success: false, error: "Failed to process payment" };
    }
  }

  /**
   * Cancel user subscription and update account tier
   */
  async cancelUserSubscription(userId: string): Promise<boolean> {
    try {
      // Update subscription status to cancelled
      const { error: subscriptionError } = await supabase
        .from("user_subscriptions")
        .update({
          status: "cancelled",
          cancelled_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", userId)
        .eq("status", "active");

      if (subscriptionError) {
        console.error("Error cancelling subscription:", subscriptionError);
        return false;
      }

      // Update account tier back to PAYG
      const tierUpdateSuccess = await this.updateUserAccountTier(
        userId,
        "PAYG",
      );

      return tierUpdateSuccess;
    } catch (error) {
      console.error("Error in cancelUserSubscription:", error);
      return false;
    }
  }

  /**
   * Ensure user has a subscription (create default if none exists)
   */
  async ensureUserHasSubscription(
    userId: string,
  ): Promise<UserSubscription | null> {
    try {
      let subscription = await this.getUserActiveSubscription(userId);

      if (!subscription) {
        console.log("No subscription found, creating default subscription...");
        subscription = await this.createDefaultSubscription(userId);
      }

      return subscription;
    } catch (error) {
      console.error("Error ensuring user has subscription:", error);
      return null;
    }
  }

  /**
   * Get user's payment transaction history
   */
  async getUserPaymentHistory(
    userId: string,
    limit: number = 10,
  ): Promise<PaymentTransaction[]> {
    try {
      const { data, error } = await supabase
        .from("payment_transactions")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) {
        console.error("Error fetching payment history:", error);
        return [];
      }

      return data as PaymentTransaction[];
    } catch (error) {
      console.error("Error in getUserPaymentHistory:", error);
      return [];
    }
  }

  /**
   * Calculate next billing date
   */
  private calculateNextBillingDate(billingCycle: "monthly" | "yearly"): string {
    const now = new Date();
    if (billingCycle === "yearly") {
      now.setFullYear(now.getFullYear() + 1);
    } else {
      now.setMonth(now.getMonth() + 1);
    }
    return now.toISOString();
  }

  /**
   * Handle webhook data for payment status updates
   */
  async processWebhookUpdate(
    providerTransactionId: string,
    status: "completed" | "failed" | "refunded",
    webhookData: Record<string, any>,
  ): Promise<boolean> {
    try {
      // Find the transaction
      const transaction = await this.getPaymentTransactionByProviderId(
        providerTransactionId,
      );

      if (!transaction) {
        console.error(
          "Transaction not found for webhook update:",
          providerTransactionId,
        );
        return false;
      }

      // Update transaction status
      await this.updatePaymentTransaction(transaction.id!, {
        status,
        webhook_data: webhookData,
        processed_at:
          status === "completed" ? new Date().toISOString() : undefined,
      });

      // If payment failed or refunded, handle subscription status
      if (status === "failed" || status === "refunded") {
        if (transaction.subscription_id) {
          await supabase
            .from("user_subscriptions")
            .update({ status: status === "refunded" ? "cancelled" : "pending" })
            .eq("id", transaction.subscription_id);

          // Update account tier back to free if payment failed/refunded
          await this.updateUserAccountTier(transaction.user_id, "free");
        }
      }

      return true;
    } catch (error) {
      console.error("Error processing webhook update:", error);
      return false;
    }
  }
}

export const subscriptionService = new SubscriptionService();
export default SubscriptionService;
